import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Col, Row } from 'react-bootstrap';
//api
import { deleteDetailOrder, getAllOrdersNotPaginated } from 'api/orders';
import { getAllOrders } from 'api/pathForHook/orders';
//helpers
import { formatDateToSpanish } from 'helpers';
//utils
import { createFilesToExport } from 'utils/backoffice/exportPDFExcel';
import useHandleSearch from 'utils/customHooks/useHandleSearch';
import { formatCurrency } from 'utils/currencyFormatter';
//components
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import CustomTable from 'components/backoffice/customTable';
import DropdownIcons from 'components/backoffice/dropDownIcons';
import Title from 'components/backoffice/texts/title';
import Button from 'components/backoffice/buttonCustom';
import StatusBudget from 'components/budget/StatusBudget';
import { usePermissions } from 'components/backoffice/PermissionsContext';
import FiltersTableOrders from './FiltersTableOrders';
import CustomCheckbox from 'components/backoffice/customCheckbox';

const Purchases = () => {
  const userPermissions = usePermissions();
  const navigate = useNavigate();
  const [listOrders, setListOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [responseInfo, setResponseInfo] = useState({});
  const { t } = useTranslation(['backoffice','contact']);
  const { setTitle } = useOutletContext();
  const [order, setOrder] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const { fetchData, stateData, loading } = useHandleSearch();
  const [formFilter, setFormFilter] = useState({
    code: '',
    usertype: '',
    benefited_company: null,
    order_status: '',
  });

  const toggleSelectAll = async (e) => {
    if (e.target.checked) {
      const { data, status } = await getAllOrdersNotPaginated();
      if (status === 200) {
        setSelectedRows(data.map((row) => row)); 
        setAllOrders(data); 
      }
    } else {
      setSelectedRows([]); 
      setAllOrders([]);
    }
  };

  const toggleSelectRow = (row) => {
    setAllOrders([]);
    setSelectedRows(
      (prev) =>
        prev.some((el) => el.id === row.id)
          ? prev.filter((el) => el.id !== row.id) 
          : [...prev, row], 
    );
  };

  const colsOrders = [
    <CustomCheckbox
      onChange={toggleSelectAll}
      checked={(selectedRows?.length || 0) === (listOrders?.length || 0) && (listOrders?.length || 0) > 0}
    />,
    t('order'),
    t('user'),
    t('library'),
    t('benefitedbookstore'),
    t('date'),
    t('amount'),
    t('state'),
    t('contact', { ns: 'contact' }),
    t('actions'),
  ];

  const getListOrders = async () => {
    try {
      const payload = {
        page,
        page_size: 10,
        code: formFilter.code || '',
        user_type: formFilter.usertype || '',
        search_benefited_company: formFilter.benefited_company?.fiscal_name || formFilter.benefited_company?.name || '',
        order_status: formFilter.order_status || '',
        fields: 'id,code,user_email,library,benefited_company,fecha,total,status,contact_info,empresa',
      };
      await fetchData(getAllOrders(), payload);
    } catch (error) {
      console.error(error);
    }
  };

  const serializerStatusOrder = (status) => {
    if (['received_customer'].includes(status)) {
      return 'success';
    } else if (['shipped'].includes(status)) {
      return 'success-light';
    } else if (['pending', 'incomplete'].includes(status)) {
      return 'warning';
    } else if (['cancelled', 'returned'].includes(status)) {
      return 'danger';
    }
    return 'info';
  };

  useEffect(() => {
    setTitle(t('pedidos'));
    return () => {
      setTitle('');
    };
  }, [setTitle]);

  useEffect(() => {
    getListOrders();
  }, [page, formFilter]);

  useEffect(() => {
    const { results, errors, totalPages, noResults } = stateData;
    if (results !== null) {
      setListOrders(results);
      setResponseInfo({ errors, totalPages, noResults });
    } else {
      setListOrders([]);
      setResponseInfo({ errors });
    }
  }, [stateData]);

  const handleClear = () => {
    setFormFilter({
      code: '',
      usertype: '',
    });
    setPage(1);
    setResponseInfo((prevResponseInfo) => ({ ...prevResponseInfo, noResults: false }));
    setResponseInfo((prevResponseInfo) => ({ ...prevResponseInfo, totalPages: responseInfo?.totalPages }));
  };

  const handleExportOrders = () => {
    const selectedData = allOrders.length > 0 ? allOrders : selectedRows;
    createFilesToExport(t, selectedData, 'xlsx', 'order');
    setSelectedRows([]);
  };

  const openModalDelete = (object) => {
    setShowDeleteModal(true);
    setOrder(object);
  };

  const onDelete = async () => {
    const { status } = await deleteDetailOrder(order.id);
    if (status === 204) {
      toast.success(t('eliminadocorrectamente'));
      setFormFilter({
        code: '',
        usertype: '',
      });
      setShowDeleteModal(false);
    } else {
      toast(`${t('error')} ${status}: ${t('mensajeerror')}`);
    }
  };

  const handleEdit = (id) => navigate(`/intranet/purchases/${id}/edit`);
  const handleDelete = (order) => openModalDelete(order);

  return (
    <div className="d-lg-grid">
      <Title text={t('pedidos')} />
      <Row className="d-flex w-md-100 mt-3">
        <Col xl={10} lg={12} md={12} xs={12} className="d-flex text-start">
          <FiltersTableOrders
            formFilter={formFilter}
            setFormFilter={setFormFilter}
            handleClear={handleClear}
            t={t}
            page={page}
            setPage={setPage}
          />
        </Col>
        <Col
          xl={2}
          lg={12}
          md={12}
          xs={12}
          className="d-flex text-end align-bottom p-xl-0 justify-content-end pt-5 pt-xl-0 gap-2"
        >
          {userPermissions.includes('pedido.__add') && (
            <>
              <Button
                variant="secondary"
                color="primary"
                onClick={() => {
                  navigate('/intranet/purchases/create');
                }}
              >
                {t('crear')}
              </Button>
              <Button
                variant="secondary"
                color="primary"
                onClick={handleExportOrders}
                disabled={selectedRows.length === 0}
              >
                {t('export')}
              </Button>
            </>
          )}
        </Col>
      </Row>
      <div className="table-responsive">
        <CustomTable
          cols={colsOrders}
          noResults={responseInfo?.noResults}
          isLoading={loading}
          page={page}
          changePage={setPage}
          totalPages={responseInfo?.totalPages}
        >
          {listOrders &&
            listOrders.map((order) => (
              <tr key={order.id} data-id={order.id}>
                <td>
                  <CustomCheckbox
                    checked={selectedRows.some((el) => el.id === order.id)}
                    onChange={() => toggleSelectRow(order)}
                  />
                </td>
                <td>#{order.code}</td>
                <td>{order.user_email}</td>
                <td>{order.library}</td>
                <td>{order.benefited_company?.name || '-'}</td>
                <td>{formatDateToSpanish(order.fecha)}</td>
                <td className="text-end">{formatCurrency(order.total)}</td>
                <td>
                  <div className="d-flex justify-content-center">
                    <StatusBudget text={order.status?.text} status={serializerStatusOrder(order.status?.id)} />
                  </div>
                </td>
                <td>
                  <div className="d-flex flex-column align-items-start justify-content-center gap-1">
                    {order.contact_info?.name ? <span>{order.contact_info.name}</span> : '-'}
                    {order.contact_info?.email_phone ? <span>{order.contact_info.email_phone}</span> : '-'}
                  </div>
                </td>
                <td className="item-dropdown text-end">
                  <DropdownIcons
                    idItem={order.id}
                    handleEdit={userPermissions.includes('pedido.__modify') && (() => handleEdit(order.id))}
                    handleDelete={userPermissions.includes('pedido.__delete') && (() => handleDelete(order))}
                  />
                </td>
              </tr>
            ))}
        </CustomTable>
      </div>
      <DeleteModalCustom
        openModal={showDeleteModal}
        setOpenModal={setShowDeleteModal}
        handleDelete={onDelete}
        deleteTitle={t('deleteorder')}
        beforeTitle={t('pedidos')}
      />
    </div>
  );
};

export default Purchases;
